import type { Unit, User } from '../../core/models/data';

type SelectorSafeUnitMetadata = Unit & {
  internal?: boolean;
  isInternal?: boolean;
  generated?: boolean;
  isGenerated?: boolean;
  shared?: boolean;
  isShared?: boolean;
  unitType?: string;
  type?: string;
  visibility?: string;
  selectorHidden?: boolean;
};

const URL_SUFFIX_CHARACTERS = /[?#&]/;
const INTERNAL_UNIT_MARKERS = new Set(['internal', 'generated', 'shared']);

const hasInternalSelectorMetadata = (unit: SelectorSafeUnitMetadata): boolean => {
  if (unit.internal || unit.isInternal || unit.generated || unit.isGenerated || unit.shared || unit.isShared || unit.selectorHidden) {
    return true;
  }

  return [unit.unitType, unit.type, unit.visibility]
    .filter((value): value is string => typeof value === 'string')
    .some(value => INTERNAL_UNIT_MARKERS.has(value.trim().toLowerCase()));
};

export const getSelectorUnitLabel = (unit: Unit): string => (typeof unit.name === 'string' ? unit.name.trim() : '');

export const isSelectorSafeUnit = (unit: Unit | null | undefined): unit is Unit => {
  if (!unit) return false;
  if (typeof unit.id !== 'string' || unit.id.trim().length === 0) return false;
  if (URL_SUFFIX_CHARACTERS.test(unit.id)) return false;

  const label = getSelectorUnitLabel(unit);
  if (label.length === 0) return false;

  const badgeLabel = label.charAt(0).trim();
  if (badgeLabel.length === 0 && !unit.logoUrl && !unit.logo && !unit.logoFileId) return false;

  return !hasInternalSelectorMetadata(unit as SelectorSafeUnitMetadata);
};

export const getSelectorSafeUnits = (units: Unit[]): Unit[] => units.filter(isSelectorSafeUnit);

export const getAllowedUnitIds = (allUnits: Unit[], currentUser: User | null): Set<string> => {
  if (!currentUser) return new Set();

  const selectorSafeUnits = getSelectorSafeUnits(allUnits);

  if (currentUser.role === 'Admin') {
    return new Set(selectorSafeUnits.map(unit => unit.id));
  }

  return new Set((currentUser.unitIds || []).filter(Boolean));
};

export const getUserScopedUnits = (allUnits: Unit[], currentUser: User | null): Unit[] => {
  const allowedIds = getAllowedUnitIds(allUnits, currentUser);
  if (allowedIds.size === 0) return [];
  return getSelectorSafeUnits(allUnits).filter(unit => allowedIds.has(unit.id));
};

export const getValidSelectedUnitIds = (
  selectedUnits: string[],
  availableUnits: Unit[]
): string[] => {
  if (!Array.isArray(selectedUnits) || selectedUnits.length === 0) return [];
  const validUnitIds = new Set(availableUnits.map(unit => unit.id));
  return selectedUnits.filter(unitId => validUnitIds.has(unitId));
};

export const shouldShowUnitSelectionGate = ({
  isAuthenticated,
  availableUnits,
  selectedUnits,
}: {
  isAuthenticated: boolean;
  availableUnits: Unit[];
  selectedUnits: string[];
}): boolean => {
  if (!isAuthenticated) return false;
  if (availableUnits.length === 0) return true;
  return getValidSelectedUnitIds(selectedUnits, availableUnits).length === 0;
};

export const buildNextSelectedUnits = (chosenUnitId: string): string[] => {
  if (!chosenUnitId || chosenUnitId.trim().length === 0) return [];
  return [chosenUnitId.trim()];
};

export const getFallbackSelectedUnitIds = (selectedUnits: string[], availableUnits: Unit[]): string[] => {
  const validSelectedUnits = getValidSelectedUnitIds(selectedUnits, availableUnits);
  if (validSelectedUnits.length > 0) return validSelectedUnits;
  if (availableUnits.length === 1 && availableUnits[0]?.id) return [availableUnits[0].id];
  return [];
};
